import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { useCreateMeasureMutation } from "metabase/api";
import { LeaveRouteConfirmModal } from "metabase/common/components/LeaveConfirmModal";
import { trackMeasureCreated } from "metabase/common/data-studio/analytics";
import { PageContainer } from "metabase/common/data-studio/components/PageContainer";
import { getDatasetQueryPreviewUrl } from "metabase/data-studio/common/utils/get-dataset-query-preview-url";
import { useMetadataToasts } from "metabase/metadata/hooks";
import { useSelector } from "metabase/redux";
import { useNavigate } from "metabase/router";
import { getMetadataWithHiddenTables } from "metabase/selectors/metadata";
import { Button } from "metabase/ui";
import * as Lib from "metabase-lib";
import type { DatasetQuery, Measure, Table } from "metabase-types/api";

import { MeasureEditor } from "../../components/MeasureEditor";
import { NewMeasureHeader } from "../../components/NewMeasureHeader";
import { useMeasureQuery } from "../../hooks/use-measure-query";
import { createInitialQueryForTable } from "../../utils/measure-query";

type NewMeasurePageProps = {
  table: Table;
  breadcrumbs: ReactNode;
  getSuccessUrl: (measure: Measure) => string;
};

export function NewMeasurePage({
  table,
  breadcrumbs,
  getSuccessUrl,
}: NewMeasurePageProps) {
  const navigate = useNavigate();
  const metadata = useSelector(getMetadataWithHiddenTables);
  const { sendSuccessToast, sendErrorToast } = useMetadataToasts();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [definition, setDefinition] = useState<DatasetQuery | null>(null);
  const [savedMeasure, setSavedMeasure] = useState<Measure | null>(null);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (table && !isInitialized.current) {
      isInitialized.current = true;
      setDefinition(createInitialQueryForTable(table, metadata));
    }
  }, [table, metadata]);

  const { query, aggregations } = useMeasureQuery(definition, metadata);

  const isDirty =
    !savedMeasure &&
    (name.trim().length > 0 ||
      description.length > 0 ||
      aggregations.length > 0);
  const isValid = name.trim().length > 0 && aggregations.length === 1;
  const previewUrl =
    aggregations.length === 1
      ? getDatasetQueryPreviewUrl(definition)
      : undefined;

  const setQuery = useCallback((newQuery: Lib.Query) => {
    setDefinition(Lib.toJsQuery(newQuery));
  }, []);

  const [createMeasure, { isLoading: isSaving }] = useCreateMeasureMutation();

  const handleSave = useCallback(async () => {
    if (!table || !definition || !isValid) {
      return;
    }
    const { data: measure, error } = await createMeasure({
      name: name.trim(),
      definition: definition,
      description: description.trim() || undefined,
    });

    if (error) {
      trackMeasureCreated("failure");
      sendErrorToast(t`Failed to create measure`);
    } else if (measure) {
      trackMeasureCreated("success", measure.id);
      setSavedMeasure(measure);
      sendSuccessToast(t`Measure created`);
    }
  }, [
    table,
    definition,
    name,
    description,
    isValid,
    createMeasure,
    sendSuccessToast,
    sendErrorToast,
  ]);

  useEffect(() => {
    if (savedMeasure) {
      navigate(getSuccessUrl(savedMeasure));
    }
  }, [savedMeasure, getSuccessUrl, navigate]);

  return (
    <PageContainer data-testid="new-measure-page" gap="xl">
      <NewMeasureHeader
        previewUrl={previewUrl}
        onNameChange={setName}
        breadcrumbs={breadcrumbs}
        actions={
          isDirty && (
            <Button
              variant="filled"
              disabled={!isValid}
              loading={isSaving}
              onClick={handleSave}
            >
              {t`Save`}
            </Button>
          )
        }
      />
      <MeasureEditor
        query={query}
        description={description}
        onQueryChange={setQuery}
        onDescriptionChange={setDescription}
      />
      <LeaveRouteConfirmModal isEnabled={isDirty && !isSaving} />
    </PageContainer>
  );
}
